import React, { useCallback, useEffect, useMemo, useRef, useContext } from 'react'
import { useHeaderBreadcrumbs, useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import PowerMeterOverview from '../components/PowerMeterOverview'
import PowerMeterSettings from '../components/PowerMeterSettings'
import { usePowerMeters, useLivePowerMeters, PowerMeterModel, routes as powerMeterRoutes } from 'src/Libs/power-meters'
import { Grid } from '@mui/material'
import type { SettingsFormValues } from 'src/Libs/power-meters'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useUserRoles } from 'src/Hooks/user'
import { getTachoGraphValues } from 'src/Libs/system-settings/utilities/default'
import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useDispatch } from 'react-redux'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useTypedSelector } from 'src/State/RootReducer'
import { PMType } from '../types'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OfflineInstallationBlankView } from 'src/Libs/offline-mode'
import BannerQueue from 'src/Libs/banners/components/BannerQueue'
import { BannerLocations } from 'src/Libs/banners/types'
import { PowerMeterSettingsSkeleton } from 'src/Routes/SystemSettings/Settings/SystemSettingsSkeletons'
import { isUndefined } from 'lodash'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { BlankViewImageVariants, BlankView } from 'src/Components/blank-view'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'
import { useFarmContext } from 'src/Elements/Farm'

interface Props {
  installationId?: string
  storageId?: string
  parentBreadcrumb: Breadcrumb
}

const ROOT_DEVICE_NAME = 'ROOT'

/**
 * Displays the Page to see all power meters for the installation
 *
 * @param installationId a id to determin wich installation is used
 * @param storageId a id to determin wich serialno is used (in a farmcontext this is the id of the farm)
 * @param parentBreadcrumb breadcrumbs to see where the user is located in the UI
 */
const PowerMeterPage: React.FC<Props> = ({ installationId, storageId, parentBreadcrumb }) => {
  const { isServiceUser, isInstaller } = useUserRoles()
  const navigate = useNavigate()
  const { childStorageId, powerMeterIndex } = useParams<{
    childStorageId: string
    powerMeterIndex: string
  }>()

  const storageSerialNo = childStorageId || storageId
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const { setTabs, setActiveTab, activeTab } = useHeaderTabs()
  const { powerMeters, updateSettings, isLoading: isPowerMetersDataLoading, error } = usePowerMeters(storageId)
  const { isFarmMasterSelected } = useFarmContext()
  const livePowerMeters = useLivePowerMeters(storageId, powerMeterIndex)
  const title = t('framework.powermeter')
  const { setBreadcrumbs } = useHeaderBreadcrumbs()
  const dispatch = useDispatch()
  const abortControllerRef = useRef<AbortController>(new AbortController())
  const installation = useTypedSelector(state => state.storage.installation)
  const userData = useTypedSelector(state => state.user.userData)
  const isOwner = Number(installation?.ownerAccountId) === userData?.ID
  const { isStorageOnline, offlineClientStorageIds } = useContext(InstallationOnlineStatusContext)
  const pageName = t('framework.powermeter')
  const accordionName = t('systemSettings.powerMeter.settings.sectionTitle')
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()

  /**
   * This function updates the Url when chaning storageid and installationid
   * It sets the url to that wich is used by a farm or of a single storage installation
   *
   * @param powerMeters all power meters
   * @param isFarmMasterSelected a boolean that indicates if this installation is a farm
   */
  const updateUrl = (powerMeters: PowerMeterModel[], isFarmMasterSelected: boolean): void => {
    const powerMeter = powerMeters.find(powerMeter => {
      const powerMeterIndexToUse = powerMeterIndex !== undefined ? parseInt(powerMeterIndex) : undefined
      return (
        powerMeter.listIndex === powerMeterIndexToUse &&
        ((isFarmMasterSelected && powerMeter.storageSerialNo === storageSerialNo) ||
          (!isFarmMasterSelected && powerMeter.storageSerialNo === storageId))
      )
    })

    if (!powerMeter) {
      const _powerMeters = powerMeters.length ? powerMeters : [{ storageSerialNo: storageId, listIndex: 0 }]
      const newUrl = isFarmMasterSelected
        ? powerMeterRoutes.getFarmDetailsRoute(installationId, storageId, _powerMeters[0].storageSerialNo, _powerMeters[0].listIndex)
        : powerMeterRoutes.getDetailsRoute(installationId, storageId, _powerMeters[0].listIndex)
      navigate(newUrl, { replace: true })
    } else if (!isFarmMasterSelected && powerMeter.storageSerialNo === storageId) {
      const newUrl = powerMeterRoutes.getDetailsRoute(installationId, storageId, powerMeter.listIndex)
      navigate(newUrl, { replace: true })
    }
  }
  const currentItem = useMemo<PowerMeterModel | undefined>(() => {
    const powerMeterIndexToUse = powerMeterIndex !== undefined ? parseInt(powerMeterIndex) : undefined
    return powerMeters.find(powerMeter => powerMeter.listIndex === powerMeterIndexToUse && powerMeter.storageSerialNo === storageSerialNo)
  }, [powerMeters, powerMeterIndex, storageSerialNo])

  const ownerCanChangeSettings = currentItem?.conditionType !== PMType.PM_TYPE_ROOT && isOwner

  const installationAccessContext = useInstallationAccessRightsContext()
  const canChangeSettings = useMemo<boolean>(
    () => (isServiceUser || isInstaller || ownerCanChangeSettings) && installationAccessContext.hasInstallationWritingRights,
    [isServiceUser, isInstaller, ownerCanChangeSettings, installationAccessContext.hasInstallationWritingRights]
  )

  const updateTabs = useCallback(
    (items: PowerMeterModel[], isFarmMasterSelected: boolean): void => {
      const pageTabs = items.map(item => {
        const tabLabel = item.deviceName === ROOT_DEVICE_NAME ? t('settings.powerMeter.rootname') : item.deviceName
        const label = isFarmMasterSelected ? `${item.storageSerialNo} - ${tabLabel}` : tabLabel
        return {
          label: label,
          href: isFarmMasterSelected
            ? powerMeterRoutes.getFarmDetailsRoute(installationId, storageId, item.storageSerialNo, item.listIndex)
            : powerMeterRoutes.getDetailsRoute(installationId, storageId, item.listIndex),
          hasDisabledStyleAlways: !isStorageOnline || (isFarmMasterSelected && offlineClientStorageIds?.includes(item.storageSerialNo ?? '')),
          disabledTooltipText: t('installation.offline-mode.tooltip-info'),
        }
      })
      setTabs(pageTabs)
    },
    [storageId, installationId, offlineClientStorageIds] //eslint-disable-line
  )

  useEffect(() => {
    dispatch(appUpdate({ title }))
    setBreadcrumbs([parentBreadcrumb, { title }])
    return () => setBreadcrumbs([])
  }, [storageId, powerMeterIndex, parentBreadcrumb, isFarmMasterSelected, title]) // eslint-disable-line

  useEffect(() => {
    const abortController = abortControllerRef.current

    return () => {
      setTabs([])
      setActiveTab(0)
      abortController.abort()
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (!isPowerMetersDataLoading) {
      updateTabs(powerMeters, isFarmMasterSelected)
      updateUrl(powerMeters, isFarmMasterSelected)
    }
  }, [powerMeters, isFarmMasterSelected, isPowerMetersDataLoading, title]) // eslint-disable-line

  useEffect(() => {
    const indexByListIndex = powerMeters.findIndex(powerMeter => {
      const powerMeterIndexToUse = powerMeterIndex !== undefined ? parseInt(powerMeterIndex) : undefined
      return powerMeter.listIndex === powerMeterIndexToUse && powerMeter.storageSerialNo === storageSerialNo
    })
    setActiveTab(indexByListIndex > -1 ? indexByListIndex : 0)
  }, [powerMeterIndex, powerMeters, storageSerialNo]) // eslint-disable-line

  const handleUpdateSettings = useCallback(
    async (values: SettingsFormValues): Promise<void> => {
      if (!currentItem) {
        console.error('No current power-meter hardware found')
        return
      }
      try {
        await updateSettings(storageSerialNo as string, currentItem.listIndex.toString(), values, currentItem.conditionType)
        const isDeviceNameChanged = values.deviceName !== currentItem.deviceName
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName, showWarningSnackbar: isDeviceNameChanged, isEmsSetting: true })

        if (isDeviceNameChanged) {
          updateTabs(
            powerMeters.map((powerMeter, index) => (index === activeTab ? { ...powerMeter, deviceName: values.deviceName } : powerMeter)),
            isFarmMasterSelected
          )
        }
      } catch (error) {
        console.error(error)
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))
        throw error
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentItem,
      updateSettings,
      storageSerialNo,
      t,
      updateTabs,
      powerMeters,
      isFarmMasterSelected,
      activeTab,
      enqueueErrorStack,
      handleDisplaySnackbarMessageBasedOnSoftwareVersion,
    ]
  )
  const isOffline = !isStorageOnline || (isFarmMasterSelected && offlineClientStorageIds?.includes(childStorageId ?? ''))
  const isDataLoading = isPowerMetersDataLoading
  const isNoDataAvailable = currentItem === undefined

  if (isFlowTheme) return null

  if (isOffline) return <OfflineInstallationBlankView />

  if (isDataLoading) return <PowerMeterSettingsSkeleton />

  if (isNoDataAvailable)
    return (
      <BlankView
        title={'systemSettings.api.no-data-available.title'}
        explanation={'systemSettings.api.no-data-available.explanation'}
        imageVariant={!isUndefined(error) ? BlankViewImageVariants.Worried : BlankViewImageVariants.Neutral}
      />
    )

  return (
    <Grid container data-qa={'power-meter-settings-main'}>
      <BannerQueue location={BannerLocations.SettingsPowerMeter} sx={{ mt: 2, ml: 2 }} />
      <Grid item xs={12} data-qa={'power-meter-settings-config'}>
        <PowerMeterSettings model={currentItem} disabled={!canChangeSettings} onUpdate={handleUpdateSettings} />
      </Grid>
      <Grid item xs={12} data-qa={'power-meter-settings-overview'}>
        <PowerMeterOverview
          model={currentItem}
          powerLevelL1={getTachoGraphValues(
            currentItem?.powerL1.values.length ?? 0,
            currentItem?.powerL1.min ?? 0,
            livePowerMeters?.data?.powerL1?.values
          )}
          powerLevelL2={getTachoGraphValues(
            currentItem?.powerL2.values.length ?? 0,
            currentItem?.powerL2.min ?? 0,
            livePowerMeters?.data?.powerL2?.values
          )}
          powerLevelL3={getTachoGraphValues(
            currentItem?.powerL3.values.length ?? 0,
            currentItem?.powerL3.min ?? 0,
            livePowerMeters?.data?.powerL3?.values
          )}
        />
      </Grid>
    </Grid>
  )
}

export default PowerMeterPage
